import DOMPurify from 'dompurify';

import { hardenAnchor, TRUSTED_LINK_STYLE } from './linkHardening';
import type { UntrustedHtmlOptions } from './sanitizeUntrustedHtml';
import { sanitizeUntrustedHtml } from './sanitizeUntrustedHtml';

const BLOCKED_SCRIPT_NAMES = [
  'cookieyes',
  'gtm',
  'facebook',
  'fbq',
  'gtag',
  'inspectlet',
  'analytics',
] as const;

const HIDDEN_ELEMENT_PATTERNS = [
  'color:transparent',
  'visibility:hidden',
  'display:\\s*none',
] as const;

const TRACKING_PIXEL_SIZE = '1';

export interface SanitizeHtmlOptions {
  untrusted?: UntrustedHtmlOptions;
}

export function sanitizeHtml(
  messageContent: string,
  options: SanitizeHtmlOptions & { untrusted: UntrustedHtmlOptions },
): string;
export function sanitizeHtml(
  messageContent: string,
  options?: SanitizeHtmlOptions,
): string | undefined;
export function sanitizeHtml(
  messageContent: string,
  options: SanitizeHtmlOptions = {},
): string | undefined {
  if (options.untrusted) {
    return sanitizeUntrustedHtml(messageContent, options.untrusted);
  }

  const isHtml = /<[a-z][\s\S]*>/i.test(messageContent);

  if (!isHtml) {
    return;
  }

  let preCleanedContent = messageContent;

  // Remove blocked tracking scripts
  for (const name of BLOCKED_SCRIPT_NAMES) {
    preCleanedContent = preCleanedContent.replace(
      new RegExp(`<script[^>]*${name}[^>]*>[\\s\\S]*?<\\/script>`, 'gi'),
      '',
    );
  }

  preCleanedContent = preCleanedContent.replace(/!important/gi, '');

  // Remove hidden elements
  for (const pattern of HIDDEN_ELEMENT_PATTERNS) {
    preCleanedContent = preCleanedContent.replace(
      new RegExp(`<div[^>]*${pattern}[^>]*>[\\s\\S]*?<\\/div>`, 'gi'),
      '',
    );
  }

  // Remove tracking pixels
  preCleanedContent = preCleanedContent
    .replace(
      new RegExp(
        `<img[^>]*width="${TRACKING_PIXEL_SIZE}"[^>]*height="${TRACKING_PIXEL_SIZE}"[^>]*>`,
        'gi',
      ),
      '',
    )
    .replace(
      new RegExp(
        `<img[^>]*height="${TRACKING_PIXEL_SIZE}"[^>]*width="${TRACKING_PIXEL_SIZE}"[^>]*>`,
        'gi',
      ),
      '',
    )
    .replace(
      new RegExp(
        `<table[^>]*width="${TRACKING_PIXEL_SIZE}"[^>]*height="${TRACKING_PIXEL_SIZE}"[^>]*>[\\s\\S]*?<\\/table>`,
        'gi',
      ),
      '',
    )
    .replace(
      new RegExp(
        `<img[^>]*src="[^"]*\\.gif"[^>]*width="${TRACKING_PIXEL_SIZE}"[^>]*>`,
        'gi',
      ),
      '',
    )
    .replace(/<img[^>]*src="[^"]*tracking[^"]*"[^>]*>/gi, '')
    .replace(/<img[^>]*src="[^"]*pixel[^"]*"[^>]*>/gi, '');

  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      hardenAnchor(node);

      if (!node.getAttribute('style')) {
        node.setAttribute('style', TRUSTED_LINK_STYLE);
      }
    }
  });

  const sanitizedHtml = DOMPurify.sanitize(preCleanedContent, {
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: [
      'script',
      'input',
      'button',
      'iframe',
      'embed',
      'object',
      'noscript',
    ],
    FORBID_ATTR: [
      'onerror',
      'onfocus',
      'onblur',
      'onclick',
      'onload',
      'onmouseover',
      'onmouseout',
      'onkeydown',
      'onkeyup',
      'onchange',
      'onsubmit',
      'id',
      'crossorigin',
      'async',
      'defer',
    ],
    ALLOWED_URI_REGEXP:
      /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  });

  DOMPurify.removeAllHooks();

  const htmlWithFixedSpacing = sanitizedHtml.replace(/<p>\s*<\/p>/gi, '');

  return htmlWithFixedSpacing;
}
